import { fetchNotificationList } from '@/js/widgets/resident-notifications/notification-api';
import { countManagerUnreadNotifications } from '@/js/widgets/resident-notifications/map-notification';
import { fetchManagerNotificationList } from '@/js/widgets/lk-manager-notifications/manager-notification-api';

const NOTIFICATIONS_NAV_SELECTOR = '[data-notifications-nav]';
const NOTIFICATIONS_PING_SELECTOR = '[data-notifications-ping]';
const NOTIFICATIONS_BADGE_SELECTOR = '[data-notifications-badge]';
const CHANGED_EVENT = 'residentNotifications:changed';
const INIT_FLAG = 'notificationsSidebarBadgeBound';

function countUnreadFromDom(root: ParentNode = document): number {
  return root.querySelectorAll('.notification-item--new:not([hidden])').length;
}

function getBarbaNamespace(root: ParentNode): string | null {
  if (root instanceof HTMLElement) {
    return (
      root.getAttribute('data-barba-namespace') ??
      root.closest('[data-barba-namespace]')?.getAttribute('data-barba-namespace') ??
      null
    );
  }

  if ('querySelector' in root) {
    return (
      root.querySelector<HTMLElement>('[data-barba-namespace]')?.getAttribute('data-barba-namespace') ??
      null
    );
  }

  return null;
}

function isManagerNotificationsArea(root: ParentNode = document): boolean {
  return getBarbaNamespace(root) === 'personal-manager-residents-area';
}

async function fetchUnreadCount(root: ParentNode = document): Promise<number> {
  try {
    if (isManagerNotificationsArea(root)) {
      const data = await fetchManagerNotificationList({ page: 1 });
      return countManagerUnreadNotifications(data.messages);
    }

    const data = await fetchNotificationList({ page: 1 });
    return Math.max(0, data.countNewMessages);
  } catch {
    return countUnreadFromDom(root);
  }
}

export function applyNotificationsSidebarBadge(root: ParentNode = document, count: number): void {
  const navItem = root.querySelector<HTMLElement>(NOTIFICATIONS_NAV_SELECTOR);
  if (!navItem) return;

  const ping = navItem.querySelector<HTMLElement>(NOTIFICATIONS_PING_SELECTOR);
  const badge = navItem.querySelector<HTMLElement>(NOTIFICATIONS_BADGE_SELECTOR);

  const hasUnread = count > 0;

  if (ping) {
    ping.hidden = !hasUnread;
  }

  if (badge) {
    badge.hidden = !hasUnread;
    badge.textContent = String(count);
  }
}

export function updateNotificationsSidebarBadge(root: ParentNode = document, count?: number): void {
  if (typeof count === 'number') {
    applyNotificationsSidebarBadge(root, count);
    return;
  }

  void fetchUnreadCount(root).then((count) => {
    applyNotificationsSidebarBadge(root, count);
  });
}

export function initNotificationsSidebarBadge(root: ParentNode = document): void {
  const pageRoot =
    root instanceof HTMLElement ? root : root.querySelector<HTMLElement>('.personal-area');
  if (!pageRoot) return;

  updateNotificationsSidebarBadge(pageRoot);

  if (pageRoot.dataset[INIT_FLAG] === 'true') {
    return;
  }

  pageRoot.dataset[INIT_FLAG] = 'true';

  window.addEventListener(CHANGED_EVENT, () => {
    updateNotificationsSidebarBadge(pageRoot);
  });
}
